import { IClientRouting, IRouterLink, Language, TranslationMode } from '@lingvo-news/contracts';
import { parseModeParam } from '../router/parseLanguageParam';

interface RouterLike {
	push(to: unknown): Promise<unknown>;
	replace(to: unknown): Promise<unknown>;
	back(): void;
}

interface RouteLike {
	params: Record<string, unknown>;
}

interface RouteRefLike {
	value: RouteLike;
}

type RouteSource = RouteLike | RouteRefLike;

/**
 * Vue Router implementation of IClientRouting
 */
export class ClientRouting extends IClientRouting {
	constructor(
		private readonly router: RouterLike,
		private readonly route: RouteSource
	) {
		super();
	}

	private getRouteLike(): RouteLike {
		const maybeRef = this.route as RouteRefLike;
		if (typeof maybeRef.value === 'object' && maybeRef.value !== null) {
			return maybeRef.value;
		}
		return this.route as RouteLike;
	}

	getRouteParam(param: string): string | undefined {
		const value = this.getRouteLike().params[param];
		return typeof value === 'string' ? value : undefined;
	}

	getRouteParams(): Record<string, string | undefined> {
		const params: Record<string, string | undefined> = {};
		for (const [key, value] of Object.entries(this.getRouteLike().params)) {
			params[key] = typeof value === 'string' ? value : undefined;
		}
		return params;
	}

	async navigate(link: IRouterLink): Promise<void> {
		await this.router.push(link as unknown);
	}

	async replace(link: IRouterLink): Promise<void> {
		await this.router.replace(link as unknown);
	}

	goBack(): void {
		this.router.back();
	}

	getHomeLink(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode, categoryId?: string): IRouterLink {
		return {
			name: 'home',
			params: {
				nativeLanguage,
				learnedLanguage,
				mode,
				...(categoryId && { categoryId })
			}
		};
	}

	getNewsListLink(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode, categoryId?: string): IRouterLink {
		return {
			name: 'news-list',
			params: {
				nativeLanguage,
				learnedLanguage,
				mode,
				...(categoryId && { categoryId })
			}
		};
	}

	getArticleLink(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode, categoryId: string, translationId: string): IRouterLink {
		return {
			name: 'article',
			params: {
				nativeLanguage,
				learnedLanguage,
				mode,
				categoryId,
				id: translationId
			}
		};
	}

	getWordCollectionLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink {
		const modeParam = parseModeParam(this.getRouteParam('mode'));
		const mode = modeParam ?? TranslationMode.Partial;
		return {
			name: 'word-collection',
			params: {
				nativeLanguage,
				learnedLanguage,
				mode
			}
		};
	}

	getWordRepeatLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink {
		const modeParam = parseModeParam(this.getRouteParam('mode'));
		const mode = modeParam ?? TranslationMode.Partial;
		return {
			name: 'word-repeat',
			params: {
				nativeLanguage,
				learnedLanguage,
				mode
			}
		};
	}

	getQuizLink(nativeLanguage: Language, learnedLanguage: Language): IRouterLink {
		return {
			name: 'quiz',
			params: {
				nativeLanguage,
				learnedLanguage
			}
		};
	}

	getUserCategoriesLink(): IRouterLink {
		return {
			name: 'user-categories'
		};
	}

	getMySettingsLink(): IRouterLink {
		return {
			name: 'my-settings'
		};
	}

	getIntroLink(): IRouterLink {
		return {
			name: 'intro'
		};
	}

	getLoginLink(): IRouterLink {
		return {
			name: 'login'
		};
	}

	getRegisterLink(): IRouterLink {
		return {
			name: 'register'
		};
	}

	getAdminLink(): IRouterLink {
		return {
			name: 'admin'
		};
	}

	getSettingsLink(): IRouterLink {
		return {
			name: 'my-settings'
		};
	}

	getCategoryLink(nativeLanguage: Language, learnedLanguage: Language, mode: TranslationMode, categoryId: string): IRouterLink {
		return this.getNewsListLink(nativeLanguage, learnedLanguage, mode, categoryId);
	}
}
